import React from "react";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input.tsx";
import { cn } from "@/lib/utils.ts";
import { PICKER_INPUT_CLASS } from "./usePickerKeyboard.ts";

interface PickerSearchInputProps extends React.ComponentProps<"input"> {
  /** Called when the × clear button is clicked. */
  onClear?: () => void;
}

/**
 * Picker search input with an integrated × clear button.
 * Applies `PICKER_INPUT_CLASS` automatically — callers don't need to import it.
 */
export const PickerSearchInput = React.forwardRef<HTMLInputElement, PickerSearchInputProps>(
  ({ onClear, className, value, ...props }, ref) => {
    const hasValue = typeof value === "string" ? value.length > 0 : !!value;

    return (
      <div className="relative">
        <Input
          ref={ref}
          value={value}
          className={cn(PICKER_INPUT_CLASS, hasValue && onClear && "pr-7", className)}
          {...props}
        />
        {hasValue && onClear && (
          <button
            type="button"
            tabIndex={-1}
            aria-label="Clear"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded-sm text-muted-foreground hover:text-foreground"
            onMouseDown={(e) => {
              e.preventDefault();
              onClear();
            }}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  },
);

PickerSearchInput.displayName = "PickerSearchInput";
